import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { IUser } from '../../user/interfaces/user.interface';
import { UserService } from '../../user/services/user.service';
import { AuthService } from '../../auth/services/auth.service';
import { TenantActionType } from '../enum/tenant-action-type.enum';
import { Store } from '@ngrx/store';
import { IAppState } from '../../store/interface/app.state.interface';
import * as DashboardActions from '../../store/tenant/dashboard.action';
import { TenantService } from './tenant.service';

@Injectable()
export class TenantActionService {
    constructor(
        private messageService: MessageService,
        private router: Router,
        private userService: UserService,
        private authService: AuthService,
        private store: Store<IAppState>,
        private tenantService: TenantService,
    ) {}

    async selectTenant(loggedInUser: IUser | null, tenantId: string): Promise<void> {
        if (!loggedInUser) return;

        const { token } = await this.authService.switchTenant({ tenantId });
        await this.landInSwitchedTenant(token, 'You have successfully selected a tenant.');
    }

    /*=====================================
      The merchant's own switch, from the store switcher.

      Names a MEMBERSHIP, never a store. Anything the client names is something
      the client can change, and a membership id is only useful to the person
      who holds it - the server proves that on every switch, so a request that
      never loaded the switcher is refused the same way.
      @param membershipId - the membership to switch into
      @return nothing
    ======================================*/
    async selectMembership(membershipId: string): Promise<void> {
        const { token } = await this.authService.switchTenant({ membershipId });
        await this.landInSwitchedTenant(token, 'You are now in this store.');
    }

    /*=====================================
      Everything that has to happen after a switch, in order.

      The token first, because every call after it needs the new one. Then the
      account, because the sidebar's features and permissions come from the
      NEW store's membership, not the old one's. Getting this order wrong
      leaves the panel showing one store's menu over another's data.
      @param token - the token the switch returned
      @param detail - the toast's message
      @return nothing
    ======================================*/
    private async landInSwitchedTenant(token: string, detail: string): Promise<void> {
        localStorage.setItem('auth_token', token);

        await this.userService.fetchLoggedInUser();
        const data = await this.tenantService.fetchTenantStatsCount();
        this.store.dispatch(DashboardActions.setTenantStatsCount({ counts: data }));

        this.messageService.add({
            severity: 'success',
            summary: 'Store Selected',
            detail,
        });
        this.router.navigate(['/']);
    }

    handlePublishTenant(
        id: string,
        setSelectedId: (id: string) => void,
        setConfirmationType: (type: TenantActionType) => void,
        openModal: () => void,
        publishType: TenantActionType,
    ): void {
        setSelectedId(id);
        setConfirmationType(publishType);
        openModal();
    }

    handleConfigureTenant(
        id: string,
        setSelectedId: (id: string) => void,
        setConfirmationType: (type: TenantActionType) => void,
        openModal: () => void,
        publishType: TenantActionType,
    ): void {
        setSelectedId(id);
        setConfirmationType(publishType);
        openModal();
    }

    handleRemoveConfiguration(
        id: string,
        setSelectedId: (id: string) => void,
        setConfirmationType: (type: TenantActionType) => void,
        openModal: () => void,
        publishType: TenantActionType,
    ): void {
        setSelectedId(id);
        setConfirmationType(publishType);
        openModal();
    }

    handleUnderMaintenanceTenant(
        id: string,
        setSelectedId: (id: string) => void,
        setConfirmationType: (type: TenantActionType) => void,
        openModal: () => void,
        publishType: TenantActionType,
    ): void {
        setSelectedId(id);
        setConfirmationType(publishType);
        openModal();
    }
}
